/**
 * useAlerts Hook - Real-time subscription to alerts table
 * Planner - Alert System
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'

const SEVERITY_TOAST = {
  critical: toast.error,
  warning: toast.warning,
  info: toast.info
}

export function useAlerts(options = {}) {
  const { includeResolved = false, includeDismissed = false } = options

  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const shownToasts = useRef(new Set())

  const fetchAlerts = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('alerts')
        .select(`
          *,
          batch:production_batches(id, batch_code, strain_id),
          strain:strains(id, code, name, origin)
        `)
        .order('created_at', { ascending: false })
        .limit(100)

      if (!includeResolved) query = query.eq('auto_resolved', false)
      if (!includeDismissed) query = query.eq('dismissed', false)

      const { data, error: fetchError } = await query
      if (fetchError) throw fetchError
      setAlerts(data || [])
    } catch (err) {
      console.error('[useAlerts] Error:', err)
      setError(err.message)
      setAlerts([])
    } finally {
      setLoading(false)
    }
  }, [includeResolved, includeDismissed])

  useEffect(() => {
    fetchAlerts()
  }, [fetchAlerts])

  useEffect(() => {
    const channel = supabase
      .channel('planner-alerts')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'alerts'
      }, (payload) => {
        console.log('[useAlerts] New alert:', payload)
        const newAlert = payload.new
        if (newAlert.toast_immediately && !shownToasts.current.has(newAlert.id)) {
          const toastFn = SEVERITY_TOAST[newAlert.severity] || toast.info
          toastFn(newAlert.title, {
            description: newAlert.message,
            duration: newAlert.severity === 'critical' ? 10000 : 5000
          })
          shownToasts.current.add(newAlert.id)
        }
        fetchAlerts()
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'alerts'
      }, () => fetchAlerts())
      .subscribe()

    return () => supabase.removeChannel(channel)
  }, [fetchAlerts])

  const markAsRead = useCallback(async (alertId) => {
    try {
      await supabase.from('alerts').update({ read: true }).eq('id', alertId)
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, read: true } : a))
    } catch (err) {
      toast.error('Failed to mark alert as read')
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    const unreadIds = alerts.filter(a => !a.read).map(a => a.id)
    if (unreadIds.length === 0) return
    try {
      await supabase.from('alerts').update({ read: true }).in('id', unreadIds)
      setAlerts(prev => prev.map(a => ({ ...a, read: true })))
      toast.success('All alerts marked as read')
    } catch (err) {
      toast.error('Failed to mark alerts as read')
    }
  }, [alerts])

  const dismissAlert = useCallback(async (alertId) => {
    try {
      await supabase.from('alerts').update({ dismissed: true, read: true }).eq('id', alertId)
      if (!includeDismissed) {
        setAlerts(prev => prev.filter(a => a.id !== alertId))
      }
    } catch (err) {
      toast.error('Failed to dismiss alert')
    }
  }, [includeDismissed])

  const unreadCount = alerts.filter(a => !a.read && !a.dismissed).length
  const criticalCount = alerts.filter(a => a.severity === 'critical' && !a.dismissed).length

  return {
    alerts,
    loading,
    error,
    unreadCount,
    criticalCount,
    refetch: fetchAlerts,
    markAsRead,
    markAllAsRead,
    dismissAlert
  }
}

export default useAlerts
